import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ChevronDown } from 'lucide-react';

const CategoryFAQ = ({ faqs, category }) => {
  const [openIndex, setOpenIndex] = React.useState(null);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': faqs.map(faq => ({
      '@type': 'Question',
      'name': faq.q,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': faq.a
      }
    }))
  };

  return (
    <>
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>
    </Helmet>
    <div className="mt-8 mb-6" data-testid="category-faq">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Frequently Asked Questions</h3>
        <div className="space-y-2">
          {faqs.map((faq, i) => (
            <div key={i} className="border rounded-lg overflow-hidden">
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full flex items-center justify-between p-4 text-left text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                data-testid={`faq-toggle-${i}`}
              >
                {faq.q}
                <ChevronDown className={`h-4 w-4 text-slate-400 shrink-0 ml-2 transition-transform ${openIndex === i ? 'rotate-180' : ''}`} />
              </button>
              {openIndex === i && (
                <div className="px-4 pb-4 text-sm text-slate-600 border-t bg-slate-50" data-testid={`faq-answer-${i}`}>
                  {faq.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export const businessFAQs = [
  { q: 'How do I find a reliable local business in Essex?', a: 'Browse our verified directory by category or town. Each listing includes reviews from real Essex residents, contact details, and business descriptions to help you make an informed choice.' },
  { q: 'Is it free to list my business on Discover Essex?', a: 'Yes, basic business listings are completely free. We also offer premium plans with enhanced visibility, featured placement, and additional marketing tools.' },
  { q: 'How do I contact a business listed on Discover Essex?', a: 'Each listing page has contact details including phone, email, and website links. You can also send enquiries directly through the platform.' },
  { q: 'Can I leave a review for a business?', a: 'Yes, registered users can leave reviews and star ratings for any business they have used. Reviews are moderated to ensure quality and authenticity.' },
];

export const jobFAQs = [
  { q: 'How do I find jobs near me in Essex?', a: 'Use the town filter to narrow results to your area, or search by job title and category. We list opportunities across Chelmsford, Colchester, Southend-on-Sea, Basildon and all Essex towns.' },
  { q: 'Is it free to post a job on Discover Essex?', a: 'Yes, basic job postings are free. Premium listings offer enhanced visibility and featured placement to attract more applicants.' },
  { q: 'What types of jobs are listed on Discover Essex?', a: 'We feature full-time, part-time, contract, temporary, and freelance positions across all industries including retail, construction, healthcare, technology, hospitality and more.' },
  { q: 'How do I apply for a job?', a: 'Click on any job listing to view the full details and application instructions. Most listings include direct contact information or a link to the employer\'s application page.' },
];

export const eventFAQs = [
  { q: 'How do I find events happening near me in Essex?', a: 'Browse events by town or category, or use the search bar. Events are listed with dates, venues, and ticket information to help you plan your visit.' },
  { q: 'Can I list my event on Discover Essex for free?', a: 'Yes, listing your event is free. Premium event listings get featured placement and enhanced visibility across the platform.' },
  { q: 'What types of events are listed on Discover Essex?', a: 'We list community events, festivals, markets, live music, charity fundraisers, family activities, networking events, exhibitions, and more across all Essex towns.' },
  { q: 'How do I get tickets for events listed here?', a: 'Ticket information and booking links are included on each event listing page. Some events are free to attend while others link to external ticketing platforms.' },
];

export const classifiedFAQs = [
  { q: 'How do I sell something on Discover Essex Marketplace?', a: 'Create a free listing with photos, a description, price, and your preferred contact method. Your item will be visible to buyers across Essex.' },
  { q: 'Is it safe to buy from the Discover Essex Marketplace?', a: 'We recommend meeting in public places for exchanges and using secure payment methods. Always inspect items before purchasing. Report any suspicious listings to our moderation team.' },
  { q: 'Can I negotiate on marketplace prices?', a: 'Many sellers accept offers. Look for the "Accepts Offers" badge on listings, or contact the seller directly to discuss pricing.' },
  { q: 'How do I report a suspicious marketplace listing?', a: 'Use the report button on any listing page. Our moderation team reviews all reports promptly and takes action against listings that violate our guidelines.' },
];

export default CategoryFAQ;
